/* eslint-disable react-refresh/only-export-components -- modale ouverte de façon impérative via `openPreview`, pas de rendu JSX direct : le Fast Refresh ne s'applique pas ici */
import React                  from 'react';
import { createPortal }       from 'react-dom';
import currentEditorStore     from '@/TokamapEditor/stores/currentEditorStore';
import { Modal }              from '@widesk-ui/components/modal/Modal';
import TokaMap                from '@/TokamapEditor/ux/TokaMap';
import View                   from '@widesk-ui/components/View';
import Segmented              from '@widesk-ui/Segmented';
import { DesktopOutlined }    from '@ant-design/icons';
import { TabletOutlined }     from '@ant-design/icons';
import { MobileOutlined }     from '@ant-design/icons';
import { CloseOutlined }      from '@ant-design/icons';
import { EyeOutlined }        from '@ant-design/icons';
import { LoadingOutlined }    from '@ant-design/icons';
import { makeAutoObservable } from 'mobx';
import { observer }           from 'mobx-react-lite';
import Tag                    from '@widesk-ui/components/Tag';
import LogoLoader             from '@/components/LogoLoader';
import ModalTitle             from '@/components/ModalTitle';
import { EntityPanelEntry }   from '@tokamap/core';
import { Shape }              from '@tokamap/core';
import buildPlacePanelEntry   from '@/TokamapEditor/helpers/placePanelEntry';
import PreviewMapsStore       from '@/TokamapEditor/stores/PreviewMapsStore';
import ProjectVersionModel    from '@models/map/ProjectVersionModel';
import { PreviewMap }         from '@/TokamapEditor/stores/PreviewMapsStore';
import { SearchItem }          from '@tokamap/core';
import { RoutePanelSelection } from '@tokamap/core';
import { DocumentRouteEndpoint } from '@tokamap/core';
import { RouteDocument }      from '@tokamap/core';
import { findRouteAcrossDocuments } from '@tokamap/core';
import { reachableDocumentIds } from '@tokamap/core';
import PreviewRouteStore      from '@/TokamapEditor/stores/PreviewRouteStore';
import useDesignTokens        from '@/hooks/useDesignTokens';
import { MIN_IMAGE_SCREEN_PX }  from '@/TokamapEditor/ux/constants';
import { SearchExternalItem }  from '@tokamap/core';
import { TokaMapInstance }     from '@tokamap/core';
import { displayedShape }      from '@/TokamapEditor/helpers/placeLinkedContent';
import { resolvePlaceLinkedContent } from '@/TokamapEditor/helpers/placeLinkedContent';
import { zoomFactorLabel }     from '@/TokamapEditor/helpers/zoomAppearance';

type DeviceSize = 'mobile' | 'tablet' | 'desktop';

// Un écran trop haut pour la modale est rogné, jamais réduit par un `transform` : MapLibre n'en
// tient pas compte pour situer le pointeur.
const DEVICE_SIZES: Record<DeviceSize, { width?: number; height?: number; label: string; caption: string; icon: React.ReactNode }> = {
	mobile: { width: 375, height: 667, label: 'Mobile', caption: 'Mobile · 375 px de large', icon: <MobileOutlined /> },
	tablet: { width: 768, height: 1024, label: 'Tablette', caption: 'Tablette · 768 px de large', icon: <TabletOutlined /> },
	desktop: { label: 'Bureau', caption: 'Bureau · pleine largeur', icon: <DesktopOutlined /> },
};

const MODAL_TOP = 20;
const MODAL_WIDTH = 'min(1240px, calc(100vw - 48px))';
const STAGE_HEIGHT = 'max(480px, calc(100vh - 160px))';

class PreviewSizeStore {
	size: DeviceSize = 'desktop';
	/** Zoom courant, en niveaux au-delà de la vue de départ : la même mesure que l'« Apparition au zoom ». */
	zoomOffset: number | null = null;

	constructor() {
		makeAutoObservable(this);
	}

	setSize(size: DeviceSize) {
		this.size = size;
	}

	setZoomOffset(zoomOffset: number | null) {
		this.zoomOffset = zoomOffset;
	}
}

const PreviewModalTitle = observer(({ store, projectVersion }: {
	store: PreviewSizeStore;
	projectVersion?: ProjectVersionModel;
}) => {
	return (
		// Troisième colonne vide : centre le sélecteur et laisse la place à la croix de fermeture.
		<div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)', alignItems: 'center' }}>
			<View row centerV gap="sm">
				<ModalTitle icon={<EyeOutlined />}>Aperçu</ModalTitle>

				{projectVersion && <Tag color="blue" style={{ marginInlineEnd: 0 }}>Publication {projectVersion.displayNumber}</Tag>}
			</View>

			<Segmented
				onMouseDown={e => e.stopPropagation()}
				value={store.size}
				onChange={value => store.setSize(value as DeviceSize)}
				options={Object.entries(DEVICE_SIZES).map(([size, { label, icon }]) => ({ value: size, icon, label }))}
			/>
		</div>
	);
});

const PreviewStage = observer(({ store, children }: React.PropsWithChildren<{ store: PreviewSizeStore }>) => {
	const { width, height, caption } = DEVICE_SIZES[store.size];
	const tokens = useDesignTokens();

	// Un seul conteneur dont seul le style change : changer d'appareil ne doit pas recréer la carte.
	const screenStyle: React.CSSProperties = width
		? { width, maxWidth: 'calc(100% - 48px)', height: `min(${height}px, calc(100% - 48px))`, borderRadius: 20 }
		: { position: 'absolute', inset: 16, borderRadius: 8 };

	return (
		<View style={{ height: STAGE_HEIGHT }}>
			<View
				flex
				center
				rel
				style={{
					flex: 1,
					minHeight: 0,
					backgroundColor: tokens.surface.page,
					backgroundImage: `radial-gradient(${tokens.border.default} 1px, transparent 1px)`,
					backgroundSize: '16px 16px',
				}}
			>
				<View
					rel
					bg="white"
					style={{
						...screenStyle,
						overflow: 'hidden',
						border: `1px solid ${tokens.border.default}`,
						boxShadow: tokens.shadow.elevated,
					}}
				>
					{children}
				</View>
			</View>

			<View
				row
				spread
				centerV
				px={20}
				h={36}
				size={12}
				color={tokens.text.secondary}
				style={{ flexShrink: 0, borderTop: `1px solid ${tokens.border.subtle}` }}
			>
				<span>La carte telle que la voient vos visiteurs</span>
				<span>{store.zoomOffset === null ? caption : `Zoom ${zoomFactorLabel(store.zoomOffset)} · ${caption}`}</span>
			</View>
		</View>
	);
});

function PreviewLoader() {
	return (
		<View abs absT={0} absL={0} w="100%" h="100%" bg="white" zIndex={10} flex center>
			<LogoLoader size={64} />
		</View>
	);
}

const PreviewModalContent = observer(({ store, previewMaps, previewRoute, projectVersion }: {
	store: PreviewSizeStore;
	previewMaps: PreviewMapsStore;
	previewRoute: PreviewRouteStore;
	projectVersion?: ProjectVersionModel;
}) => {
	const tokens = useDesignTokens();
	// Tant que `false`, masque le conteneur MapLibre brut derrière un spinner — sans ça, le style de
	// base de la carte (voir DEFAULT_STYLE/`captureAndStripStyleLayers` dans `@tokamap/core`, un fond
	// générique façon OpenStreetMap avec rues/bâtiments/libellés, retiré seulement une fois la carte
	// chargée) reste visible plusieurs frames pendant le chargement, contrairement à l'éditeur
	// principal où `LoadingScreen` (voir `TokamapEditor/index.tsx`) joue déjà ce rôle.
	const [ready, setReady] = React.useState(false);
	// Hors état React : sert uniquement à piloter la carte au clic sur un résultat, aucun rendu n'en
	// dépend.
	const instanceRef = React.useRef<TokaMapInstance | null>(null);
	const lastSize = React.useRef(store.size);
	const sizeAtCreation = React.useRef(store.size);

	const recenterAfterResize = () => {
		requestAnimationFrame(() => requestAnimationFrame(() => instanceRef.current?.resetView()));
	};

	React.useEffect(() => {
		if (store.size === lastSize.current) return;

		lastSize.current = store.size;
		recenterAfterResize();
	}, [store.size]);

	const fullscreenElement = useFullscreenElement();
	// Le plein écran n'affiche que la carte : le bandeau la suit, sans quoi il disparaîtrait avec le reste de l'aperçu.
	const routeBanner = (previewRoute.error || previewRoute.pending || previewRoute.isActive) && (
		<View abs absB={16} absL={0} w="100%" flex center zIndex={5} style={{ pointerEvents: 'none' }}>
			<View
				row
				center
				gap="sm"
				px={14}
				py={8}
				br={999}
				bg={tokens.surface.elevated}
				color={previewRoute.error ? tokens.color.error : tokens.text.default}
				size={13}
				style={{ boxShadow: tokens.shadow.elevated, maxWidth: '85%', pointerEvents: 'auto', userSelect: 'none' }}
			>
				{previewRoute.pending && !previewRoute.error && <LoadingOutlined />}

				<span style={{ textAlign: 'center' }}>
					{previewRoute.error
						|| previewRoute.pending
						|| (previewRoute.nextMapLabel
							? `Empruntez le passage surligné pour rejoindre ${previewRoute.nextMapLabel}`
							: `Dernière étape de l'itinéraire`)}
				</span>

				<CloseOutlined
					title="Effacer l'itinéraire"
					aria-label="Effacer l'itinéraire"
					style={{ cursor: 'pointer', color: tokens.text.muted }}
					onClick={() => dismissRoute(instanceRef.current, previewRoute)}
				/>
			</View>
		</View>
	);

	return (
		<PreviewStage store={store}>
			{!ready && <PreviewLoader />}

			<TokaMap
				config={{
					loadJson: async () => {
						// Tout le projet, pas seulement la carte ouverte dans l'éditeur : la recherche et les
						// liaisons entre cartes portent sur l'ensemble. `currentJson` est celui de la carte
						// affichée, c'est-à-dire celle d'où l'aperçu a été ouvert.
						//
						// Avec une publication, c'est le projet tel qu'il était EN LIGNE à ce moment-là qui est
						// chargé — toutes ses cartes, y compris celles que cette publication n'avait pas mises
						// à jour (voir `PreviewMapsStore.load`).
						await previewMaps.load(currentEditorStore.map?.id, projectVersion);

						const json = previewMaps.currentJson;

						fieldDefinitionsById = (json as {
							extras?: { filters?: { fields?: Record<string, FieldDefinition> } }
						})?.extras?.filters?.fields ?? {};

						return json;
					},
					// resolveImageUrl: resolveAkamaiImageUrl,
					resolveLinkedContent: resolvePlaceLinkedContent,
					minImageOverlayScreenPx: MIN_IMAGE_SCREEN_PX,
					includeImages: true,
					includeIndirectImages: true,
					fpsMeter: true,
					search: {
						getSearchItem: shape => {
							const item = searchItemOf(shape, previewMaps);

							if (!item) return null;

							// La carte n'est nommée que s'il y en a plusieurs : sur un projet mono-carte, la
							// précision serait du bruit sur chaque ligne.
							return previewMaps.hasSeveralMaps
								? { ...item, subTitle: withMapLabel(item.subTitle, previewMaps.currentMap?.label) }
								: item;
						},
						// Toutes les autres cartes du projet, sans condition : chercher un lieu n'est pas s'y
						// rendre. Mêmes règles d'affichage que sur la carte affichée, mais aucune forme
						// chargée derrière ces résultats tant qu'on n'a pas basculé sur leur carte.
						getExternalItems: () => previewMaps.otherMaps.flatMap(map => externalItemsOfMap(map, previewMaps)),
						getCategories: () => previewMaps.placeTypes,
						onSelectExternalItem: payload => openExternalResult(instanceRef.current, previewMaps, payload),
					},
					entityPanel: { getEntityPanelEntry },
					routePanel: {
						// Seulement les cartes qu'un itinéraire peut relier à celle affichée, contrairement à la
						// recherche : sans géolocalisation on ne saurait pas situer le visiteur, et sans
						// liaison dans les deux sens le calcul n'aurait pas de point d'arrivée.
						getExternalItems: () => previewMaps.otherRoutableMaps.flatMap(map => externalItemsOfMap(map, previewMaps)),
						// Demander un itinéraire met fin au précédent : le core efface son tracé, à nous
						// d'oublier les tronçons et l'étape en cours, sinon le bandeau continuerait d'annoncer
						// un trajet qui n'est plus affiché.
						onStart: () => previewRoute.clear(),
						// Le calcul quitte `@tokamap/core`, qui ne connaît qu'un document à la fois.
						onValidate: (from, to) => startRoute(instanceRef.current, previewMaps, previewRoute, from, to),
					},
					documentSwitcher: {
						getDocuments: () => previewMaps.maps.map(map => ({ id: map.mapId, label: map.label })),
						getCurrentDocumentId: () => previewMaps.currentMapId,
						onSelect: mapId => {
							if (instanceRef.current) openLinkedMap(instanceRef.current, previewMaps, previewRoute, mapId);
						},
					},
				}}
				onReady={instance => {
					instanceRef.current = instance;
					setReady(true);

					if (store.size !== sizeAtCreation.current) recenterAfterResize();

					const showZoom = (zoom: number) => {
						const startZoom = instance.mapAdapter.getStartCamera()?.zoom;

						store.setZoomOffset(startZoom === undefined ? null : zoom - startZoom);
					};

					showZoom(instance.mapAdapter.getZoom());
					instance.mapAdapter.on(event => {
						if (event.type === 'map-view:changed') showZoom(event.view.zoom);
					});

					// Le clic sur une zone liée à une carte — le zaap. `@tokamap/core` ne l'interprète pas
					// lui-même (contrairement à une redirection web) : lui seul ne saurait pas où trouver
					// le document visé.
					instance.mapAdapter.on(event => {
						if (event.type !== 'shape:click') return;

						const shape = instance.shapeStore.getAll().find(s => s.id === event.shapeId);

						if (shape?.action !== 'open_map') return;

						// Un seul geste, qu'on suive l'itinéraire ou qu'on s'en écarte : c'est la carte
						// d'arrivée qui dit quel tronçon reprendre (voir `openLinkedMap`).
						openLinkedMap(instance, previewMaps, previewRoute, shape.targetDocumentId);
					});
				}}
			/>

			{/* Ce que `@tokamap/core` ne peut pas dire lui-même : il ne connaît qu'une carte à la fois,
			    donc ni l'étape suivante ni pourquoi un itinéraire inter-cartes a échoué. Le panneau
			    d'itinéraire, lui, reste réservé au choix du départ et de l'arrivée — et disparaît au
			    premier changement de carte, ce qui fait de ce bandeau le seul endroit d'où l'on peut
			    encore se débarrasser d'un itinéraire. */}
			{fullscreenElement ? createPortal(routeBanner, fullscreenElement) : routeBanner}
		</PreviewStage>
	);
});

/**
 * Ouvre l'aperçu du projet : tel qu'il est aujourd'hui, ou tel qu'il était en ligne à la publication
 * passée en argument.
 */
export async function openPreview(projectVersion?: ProjectVersionModel) {
	const store = new PreviewSizeStore();
	// Une instance par ouverture : l'aperçu repart des cartes telles qu'elles sont à cet instant.
	const previewMaps = new PreviewMapsStore();
	const previewRoute = new PreviewRouteStore(previewMaps);

	Modal.open({
		title: <PreviewModalTitle store={store} projectVersion={projectVersion} />,
		footer: null,
		top: MODAL_TOP,
		width: MODAL_WIDTH,
		bodyPadding: 0,
		// Créer la carte pendant l'animation d'ouverture la fait saccader : le même cadre, avec son
		// loader, la remplace le temps de l'animation.
		transitionContent: (
			<PreviewStage store={store}>
				<PreviewLoader />
			</PreviewStage>
		),
		children: (
			<PreviewModalContent
				store={store}
				previewMaps={previewMaps}
				previewRoute={previewRoute}
				projectVersion={projectVersion}
			/>
		),
	});
}

/**
 * Entrée de recherche d'une forme, qu'elle appartienne à la carte affichée ou à une autre — seules
 * les formes liées à un point d'intérêt sont cherchables, une forme sans point d'intérêt (ex. un
 * décor, ou une zone qui ouvre une autre carte) n'a rien à proposer.
 */
function searchItemOf(shape: Shape, previewMaps?: PreviewMapsStore): SearchItem | null {
	const place = (shape.entity as {
		entity?: { id?: number; label?: string; reference?: string; location?: string }
	} | undefined)?.entity;

	if (!place?.label) return null;

	return {
		title: place.label,
		subTitle: place.reference,
		searchText: place.location,
		image: imageOf(shape),
		key: place.id !== null ? String(place.id) : undefined,
		categoryId: previewMaps?.placeTypeIdOf(place.id),
	};
}

/** Nom de la carte ajouté au sous-titre du résultat, pour que le visiteur sache où il va atterrir. */
function withMapLabel(subTitle: string | undefined, mapLabel: string | undefined): string | undefined {
	if (!mapLabel) return subTitle;

	return subTitle ? `${subTitle} · ${mapLabel}` : mapLabel;
}

/** Ce qu'un résultat d'une autre carte doit retenir pour qu'on sache l'atteindre au clic. */
interface ExternalResultPayload {
	mapId: id;
	/** Le point d'intérêt visé, PAS une forme : voir `openExternalResult`. */
	key: string;
}

/**
 * Les points d'intérêt d'une carte non affichée, dédupliqués comme `@tokamap/core` le fait pour la
 * carte affichée : plusieurs formes découpant un même point d'intérêt ne donnent qu'une ligne.
 */
function externalItemsOfMap(map: PreviewMap, previewMaps: PreviewMapsStore): SearchExternalItem[] {
	const itemsByKey = new Map<string, SearchExternalItem>();

	map.shapes.forEach(shape => {
		const item = searchItemOf(shape, previewMaps);

		if (!item) return;

		const key = item.key ?? shape.id;

		if (itemsByKey.has(key)) return;

		itemsByKey.set(key, {
			title: item.title,
			subTitle: withMapLabel(item.subTitle, map.label),
			searchText: item.searchText,
			image: item.image,
			categoryId: item.categoryId,
			payload: { mapId: map.mapId, key } satisfies ExternalResultPayload,
		});
	});

	return [...itemsByKey.values()];
}

/**
 * Bascule sur la carte du résultat cliqué, puis surligne le point d'intérêt trouvé. Les formes sont
 * retrouvées par leur point d'intérêt et non par un identifiant relevé à l'avance : une forme dont
 * le JSON ne porte pas d'identifiant en reçoit un nouveau à chaque analyse du document (voir
 * `PreviewMap.shapes`), celui qu'a la forme une fois chargée n'est donc pas celui qu'on avait lu.
 */
async function openExternalResult(
	instance: TokaMapInstance | null,
	previewMaps: PreviewMapsStore,
	payload: unknown,
) {
	const { mapId, key } = payload as ExternalResultPayload;
	const map = previewMaps.maps.find(m => m.mapId === mapId);

	if (!instance || !map) return;

	previewMaps.setCurrentMapId(mapId);

	await instance.loadDocument(map.json);

	const shapeIds = instance.shapeStore.getAll()
		.filter(shape => {
			const item = searchItemOf(shape);

			return !!item && (item.key ?? shape.id) === key;
		})
		.map(shape => shape.id);

	if (shapeIds.length) instance.highlightShapes(shapeIds, { zoomToFit: true });
}

/**
 * Efface l'itinéraire affiché, à la demande du visiteur. Le tracé et le surlignage sont posés sur la
 * carte, pas seulement retenus ici : les retirer demande de passer par l'instance.
 */
function dismissRoute(instance: TokaMapInstance | null, previewRoute: PreviewRouteStore) {
	previewRoute.clear();
	instance?.mapAdapter.clearRoute();
	instance?.highlightShapes([]);
}

/**
 * Extrémité d'itinéraire telle que le calcul inter-cartes l'attend : un point, plus la carte où il
 * se trouve. `null` quand l'extrémité n'est pas résolvable — position du visiteur inconnue, ou
 * point d'intérêt introuvable sur la carte annoncée.
 */
async function toDocumentEndpoint(
	selection: RoutePanelSelection,
	instance: TokaMapInstance,
	previewMaps: PreviewMapsStore,
): Promise<DocumentRouteEndpoint | null> {
	const currentMapId = previewMaps.currentMapId;

	if (!currentMapId) return null;

	if (selection.type === 'user-location') {
		// Attendue plutôt que lue : un premier relevé met parfois plusieurs dizaines de secondes à
		// arriver (voir `MapAdapter.waitForUserLocation`).
		const position = await instance.mapAdapter.waitForUserLocation();

		// Rien ne dit sur quelle carte se trouve le visiteur : on le suppose sur celle qu'il regarde.
		return position ? { documentId: currentMapId, endpoint: { type: 'position', position } } : null;
	}

	if (selection.type === 'external') {
		const { mapId, key } = selection.payload as ExternalResultPayload;
		const map = previewMaps.maps.find(m => m.mapId === mapId);
		const shape = map?.shapes.find(s => {
			const item = searchItemOf(s);

			return !!item && (item.key ?? s.id) === key;
		});

		return shape ? { documentId: mapId, endpoint: { type: 'shape', shapeId: shape.id } } : null;
	}

	return { documentId: currentMapId, endpoint: { type: 'shape', shapeId: selection.shapeId } };
}

/**
 * Les cartes telles que le calcul les voit. La carte affichée est décrite par ses formes CHARGÉES,
 * pas par celles analysées à l'ouverture : c'est parmi les premières que le panneau d'itinéraire a
 * choisi son départ, et deux analyses d'un même document ne donnent pas les mêmes identifiants aux
 * formes dont le JSON n'en porte pas (voir `PreviewMap.shapes`).
 */
function routeDocuments(instance: TokaMapInstance, previewMaps: PreviewMapsStore): RouteDocument[] {
	return previewMaps.maps.map(map => ({
		documentId: map.mapId,
		shapes: map.mapId === previewMaps.currentMapId ? instance.shapeStore.getAll() : map.shapes,
	}));
}

/** Calcule l'itinéraire demandé et affiche son premier tronçon. */
async function startRoute(
	instance: TokaMapInstance | null,
	previewMaps: PreviewMapsStore,
	previewRoute: PreviewRouteStore,
	from: RoutePanelSelection,
	to: RoutePanelSelection,
) {
	if (!instance) return;

	const run = previewRoute.begin();

	instance.mapAdapter.clearRoute();
	instance.highlightShapes([]);

	// Sans un mot dès la validation, le visiteur croit qu'elle n'a rien déclenché.
	previewRoute.wait(
		from.type === 'user-location' && instance.mapAdapter.getUserLocation() === null
			? 'Recherche de votre position…'
			: 'Calcul de l\'itinéraire…',
	);

	const [fromEndpoint, toEndpoint] = await Promise.all([
		toDocumentEndpoint(from, instance, previewMaps),
		toDocumentEndpoint(to, instance, previewMaps),
	]);

	// Effacé ou relancé pendant qu'on attendait : ce résultat n'intéresse plus personne.
	if (!previewRoute.isCurrent(run)) return;

	if (!fromEndpoint || !toEndpoint) {
		// Le cas courant, et le seul sur lequel le visiteur peut agir : il a choisi « Ma position »
		// alors que le navigateur n'a pas encore livré de relevé, ou que l'autorisation a été refusée.
		const positionInconnue =
			(from.type === 'user-location' || to.type === 'user-location')
			&& instance.mapAdapter.getUserLocation() === null;

		previewRoute.fail(
			positionInconnue
				// Le service de localisation du navigateur répond parfois en une seconde, parfois pas du
				// tout : réessayer suffit souvent, et partir d'un point de la carte contourne le problème.
				? `Votre position n'a pas pu être déterminée. Réessayez, ou partez d'un point de la carte.`
				: `Départ ou arrivée introuvable.`,
		);

		return;
	}

	previewRoute.wait('Calcul de l\'itinéraire…');

	// Le calcul est synchrone et bloque la page : sans laisser passer un rendu, l'annonce ne
	// s'afficherait qu'une fois le calcul terminé.
	await nextPaint();

	if (!previewRoute.isCurrent(run)) return;

	const documents = routeDocuments(instance, previewMaps);
	const legs = findRouteAcrossDocuments({ documents, from: fromEndpoint, to: toEndpoint });

	if (!legs) {
		previewRoute.fail(routeFailureMessage(documents, fromEndpoint, toEndpoint));
		return;
	}

	previewRoute.start(legs);

	await showCurrentLeg(instance, previewMaps, previewRoute);
}

function nextPaint(): Promise<void> {
	return new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve)));
}

/**
 * Pourquoi aucun itinéraire n'a été trouvé. Le calcul ne le dit pas lui-même — il rend `null` sans
 * distinguer —, mais les deux causes n'appellent pas du tout la même réaction : compléter le projet
 * en posant une zone de liaison, ou libérer un passage sur la carte. Les départager ici revient à
 * reposer au graphe la question qu'il sait trancher (voir `reachableDocumentIds`), plutôt que de
 * supposer d'après la seule différence des cartes.
 */
function routeFailureMessage(
	documents: RouteDocument[],
	from: DocumentRouteEndpoint,
	to: DocumentRouteEndpoint,
): string {
	if (from.documentId === to.documentId) return `Aucun itinéraire trouvé sur cette carte.`;

	return reachableDocumentIds(documents, from.documentId).has(to.documentId)
		? `Aucun chemin praticable jusqu'à cette destination.`
		: `Ces deux cartes ne sont reliées par aucun passage.`;
}

/**
 * Trace le tronçon courant, en basculant d'abord sur sa carte si besoin, et signale le passage par
 * lequel on en sort. Le surlignage tient lieu d'indication : c'est la zone sur laquelle le visiteur
 * doit cliquer pour continuer.
 */
async function showCurrentLeg(
	instance: TokaMapInstance,
	previewMaps: PreviewMapsStore,
	previewRoute: PreviewRouteStore,
) {
	const leg = previewRoute.currentLeg;

	if (!leg) return;

	if (leg.documentId !== previewMaps.currentMapId) {
		const map = previewMaps.maps.find(m => m.mapId === leg.documentId);

		if (!map) return;

		previewMaps.setCurrentMapId(map.mapId);

		await instance.loadDocument(map.json);
	}

	const exitLabel = leg.exitShapeId ? previewRoute.nextMapLabel : undefined;

	instance.mapAdapter.showRoute(leg.coordinates, exitLabel ? { exitLabel } : undefined);

	// La forme peut manquer quand le document rechargé a renuméroté ses formes (voir
	// `routeDocuments`) : le tracé reste affiché, seule l'indication du passage se perd.
	if (leg.exitShapeId) instance.highlightShapes([leg.exitShapeId]);
}

/**
 * Emmène le visiteur sur la carte liée à la zone cliquée. La carte s'ouvre sur sa vue par défaut :
 * le zaap ne désigne qu'une carte, pas un endroit précis sur celle-ci.
 *
 * Un itinéraire en cours lui survit : si le trajet passe par la carte d'arrivée, son tronçon s'y
 * retrace. C'est ce qui fait qu'avancer et revenir en arrière se ressemblent — dans les deux cas on
 * retrouve le trajet là où il passe, plutôt que de le perdre en s'écartant.
 */
async function openLinkedMap(
	instance: TokaMapInstance,
	previewMaps: PreviewMapsStore,
	previewRoute: PreviewRouteStore,
	documentId?: string,
) {
	const map = previewMaps.maps.find(m => m.mapId === documentId);

	// Carte cible supprimée du projet, ou jamais choisie : la zone ne fait rien plutôt que d'emmener
	// le visiteur nulle part.
	if (!map) return;

	previewMaps.setCurrentMapId(map.mapId);

	await instance.loadDocument(map.json);

	// Carte à l'écart du trajet : rien à tracer, mais l'itinéraire reste en mémoire et réapparaîtra
	// dès qu'on repassera sur une carte qu'il emprunte.
	if (previewRoute.focusMap(map.mapId)) showCurrentLeg(instance, previewMaps, previewRoute);
}

interface FieldDefinition {
	label: string;
	fieldType: string;
	reference: string;
}

let fieldDefinitionsById: Record<string, FieldDefinition> = {};

function getEntityPanelEntry(shape: Shape): EntityPanelEntry | null {
	const place = (
		shape.entity as
			| {
			entity?: {
				label?: string;
				reference?: string;
				location?: string;
				fields?: Record<string, unknown>;
			};
		}
			| undefined
	)?.entity;
	if (!place?.label) return null;

	// Simple adaptation vers l'entrée du panneau : les règles d'affichage (titre remplacé, images
	// affichées, champs vides masqués) sont dans `buildPlacePanelEntry`, partagées avec l'aperçu de la
	// fiche d'un point d'intérêt.
	const entry = buildPlacePanelEntry({
		label: place.label,
		location: place.location,
		reference: place.reference,
		// Un champ sans définition connue n'est pas affichable : ni intitulé, ni nature.
		fields: Object.entries(place.fields ?? {}).flatMap(([fieldId, value]) => {
			const definition = fieldDefinitionsById[fieldId];

			if (!definition) return [];

			return [{
				label: definition.label,
				reference: definition.reference,
				fieldType: definition.fieldType,
				value,
			}];
		}),
	});

	return {
		...entry,
		image: imageOf(shape),
	};
}

function imageOf(shape: Shape): string | undefined {
	return displayedShape(shape).image?.src || undefined;
}

function useFullscreenElement(): Element | null {
	const [element, setElement] = React.useState<Element | null>(() => document.fullscreenElement);

	React.useEffect(() => {
		const update = () => setElement(document.fullscreenElement);

		document.addEventListener('fullscreenchange', update);

		return () => document.removeEventListener('fullscreenchange', update);
	}, []);

	return element;
}
